import React from "react";
import { Link } from "react-router-dom";
import { Card, CardBody } from "reactstrap";
import { designRegistry } from "./DashboardDesignWidgets";

export const Icon = ({ name }) => (
  <i className={`bx ${name}`} aria-hidden="true" />
);

const metrics = {
  "total-engagements": [
    "bx-folder-open",
    "totalEngagements",
    "Total Engagements",
    "blue",
  ],
  "active-engagements": [
    "bx-calendar-check",
    "inProgress",
    "In Progress",
    "green",
  ],
  "assigned-to-me": ["bx-user-check", "assignedToMe", "Assigned to Me", "blue"],
  "high-risk": ["bx-bookmark-alt", "highRisk", "High Risk", "red"],
  "overdue-reviews": [
    "bx-time-five",
    "overdueReviews",
    "Overdue Reviews",
    "red",
  ],
  "open-findings": [
    "bx-error-circle",
    "openFindings",
    "Open Findings",
    "orange",
  ],
  "open-queries": [
    "bx-question-mark",
    "openQueries",
    "Queries Pending",
    "orange",
  ],
  "completed-reviews": [
    "bx-badge-check",
    "completedThisMonth",
    "Completed This Month",
    "green",
  ],
  "upcoming-deadlines-kpi": [
    "bx-calendar",
    "upcomingDeadlines",
    "Upcoming Deadlines",
    "blue",
  ],
};

const Panel = ({ title, action, children }) => (
  <Card className="ops-panel">
    <CardBody>
      <div className="ops-panel__header">
        <h2>{title}</h2>
        {action && (
          <button type="button" className="ops-text-action">
            {action}
          </button>
        )}
      </div>
      {children}
    </CardBody>
  </Card>
);

const MetricWidget = ({ widget, data }) => {
  const [icon, key, label, tone] =
    metrics[widget.type] || metrics["total-engagements"];
  const value =
    key === "upcomingDeadlines"
      ? data?.deadlines?.length
      : data?.metrics?.[key];
  return (
    <Card className="ops-metric">
      <CardBody>
        <div className={`ops-metric__icon ops-metric__icon--${tone}`}>
          <Icon name={icon} />
        </div>
        <div>
          <span>{label}</span>
          <strong>{Number(value || 0)}</strong>
          <small>Current firm data</small>
        </div>
      </CardBody>
    </Card>
  );
};

const InspectionPipeline = ({ data }) => {
  const icons = [
    "bx-clipboard",
    "bx-folder",
    "bx-calendar-check",
    "bx-archive-in",
    "bx-badge-check",
  ];
  const tones = ["blue", "cyan", "green", "amber", "green"];
  const steps = data?.pipeline || [];
  return (
    <Panel title="Inspection Pipeline">
      <div className="ops-pipeline">
        {steps.map((step, index) => (
          <React.Fragment key={step.label}>
            <div className="ops-pipeline__step">
              <i
                className={`ops-pipeline__icon ops-pipeline__icon--${
                  tones[index % tones.length]
                }`}
              >
                <Icon name={icons[index % icons.length]} />
              </i>
              <span>{step.label}</span>
              <strong>{step.value}</strong>
            </div>
            {index < steps.length - 1 && (
              <i className="ops-pipeline__line">
                <Icon name="bx-right-arrow-alt" />
              </i>
            )}
          </React.Fragment>
        ))}
      </div>
      {!steps.length && <p>No pipeline data is available.</p>}
    </Panel>
  );
};

const RiskDistribution = ({ widget, data }) => {
  const levels = widget.configuration?.riskLevels || [
    "high",
    "medium",
    "low",
    "notRated",
  ];
  const total = (data?.risks || []).reduce(
    (sum, row) => sum + Number(row.value || 0),
    0
  );
  const rows = (data?.risks || [])
    .map((row) => ({
      ...row,
      key: row.label.toLowerCase().replace(/\s+/g, ""),
    }))
    .filter((row) => levels.includes(row.key));
  const isBar = widget.configuration?.chartType === "bar";
  return (
    <Panel title="Risk Distribution">
      <div className={`ops-risk ${isBar ? "ops-risk--bar" : ""}`}>
        {!isBar && (
          <div className="ops-donut">
            <div>
              <strong>{total}</strong>
              <span>Total</span>
            </div>
          </div>
        )}
        <div className="ops-risk__legend">
          {rows.map((row) => (
            <div key={row.key}>
              <i className={`is-${row.key}`} />
              <span>{row.label}</span>
              <b>
                {row.value} ({total ? Math.round((row.value / total) * 100) : 0}
                %)
              </b>
            </div>
          ))}
        </div>
      </div>
    </Panel>
  );
};

const UpcomingDeadlines = ({ widget, data }) => {
  const maxItems = widget.configuration?.maxItems || 5;
  const includeOverdue = widget.configuration?.includeOverdue !== false;
  const rows = (data?.deadlines || [])
    .filter((item) => includeOverdue || new Date(item.dueDate) >= new Date())
    .slice(0, maxItems);
  return (
    <Panel title="Upcoming Deadlines" action="View Calendar">
      <div className="ops-deadlines">
        {rows.map((item) => (
          <article key={item.id}>
            <i>
              <Icon name="bx-calendar" />
            </i>
            <div>
              <strong>{item.number}</strong>
              <span>
                {item.name} – {item.client}
              </span>
            </div>
            <time>
              <b>
                {new Date(item.dueDate) < new Date() ? "Overdue" : "Upcoming"}
              </b>
              <span>{new Date(item.dueDate).toLocaleDateString()}</span>
            </time>
            <em
              className={`ops-pill ops-pill--${
                item.priority > 1 ? "red" : "green"
              }`}
            >
              {item.reviewType}
            </em>
          </article>
        ))}
      </div>
      <button className="ops-link" type="button">
        View all deadlines <Icon name="bx-right-arrow-alt" />
      </button>
    </Panel>
  );
};

const WorkloadByReviewer = ({ data }) => {
  const reviewers = data?.reviewers || [];
  return (
    <Panel title="Workload by Reviewer" action="View All">
      <div className="ops-reviewer ops-reviewer--heading">
        <span>Reviewer</span>
        <span>In Progress</span>
        <span>Review</span>
        <span>Total</span>
        <span>Capacity</span>
      </div>
      {reviewers.map((item) => (
        <div className="ops-reviewer" key={item.name}>
          <span className="ops-reviewer__name">
            <i>{item.initials}</i>
            {item.name}
          </span>
          <span>{item.inProgress}</span>
          <span>{item.review}</span>
          <span>{item.total}</span>
          <span>
            <b>{item.capacity}%</b>
            <em>
              <i style={{ width: `${item.capacity}%` }} />
            </em>
          </span>
        </div>
      ))}
    </Panel>
  );
};

const QueryAnalytics = ({ widget, data }) => {
  const config = widget.configuration || {};
  const buckets = config.agingBuckets || ["0-7", "8-14", "15-30", "30+"];
  const queryData = data?.queries || {};
  const total = Number(queryData.open || 0);
  const rows = (queryData.aging || []).filter((row) =>
    buckets.includes(row.label)
  );
  return (
    <Panel title="Query Analytics" action="View Full Report">
      <div className="ops-query-summary">
        <div>
          <strong>{total}</strong>
          <span>Open Queries</span>
        </div>
        {config.displayResponseTime !== false && (
          <div>
            <strong>
              {Number(queryData.averageResponseDays || 0).toFixed(1)}
            </strong>
            <span>Avg. Response Time (days)</span>
          </div>
        )}
      </div>
      {config.displayQueryAging !== false && (
        <>
          <h3>Query Aging</h3>
          <div className="ops-aging">
            {rows.map((row, index) => (
              <div key={row.label}>
                <span>{row.label} days</span>
                <em>
                  <i
                    className={`is-${
                      ["green", "amber", "orange", "red"][index]
                    }`}
                    style={{
                      width: `${total ? (row.value / total) * 100 : 0}%`,
                    }}
                  />
                </em>
                <b>
                  {row.value} (
                  {total ? Math.round((row.value / total) * 100) : 0}%)
                </b>
              </div>
            ))}
          </div>
        </>
      )}
    </Panel>
  );
};

const InspectionCycleProgress = ({ data }) => (
  <Panel title="Inspection Cycle Progress" action="View All">
    <div className="ops-cycle">
      {(data?.cycles || []).map((item) => {
        const value = item.total
          ? Math.round((item.completed / item.total) * 100)
          : 0;
        return (
          <article key={item.name}>
            <div>
              <strong>{item.name}</strong>
              <span>
                {item.startDate
                  ? new Date(item.startDate).toLocaleDateString()
                  : "Not set"}{" "}
                –{" "}
                {item.endDate
                  ? new Date(item.endDate).toLocaleDateString()
                  : "Not set"}
              </span>
            </div>
            <em>
              <i style={{ width: `${value}%` }} />
            </em>
            <b>
              {value}%
              <small>
                {item.completed} / {item.total}
              </small>
            </b>
          </article>
        );
      })}
    </div>
  </Panel>
);

const MyWorkWidget = ({ widget }) => {
  const assignments = widget.type === "my-assignments";
  return (
    <Panel title={assignments ? "My Assignments" : "Reviews Awaiting Me"}>
      <p>
        {assignments
          ? "Open your assigned engagements to continue preparation and track your work."
          : "Open your review tasks to see the reviews available to you."}
      </p>
      <Link
        className="ops-link"
        to={assignments ? "/engagements/assigned" : "/review-tasks"}
      >
        {assignments ? "View my engagements" : "View my review tasks"}{" "}
        <Icon name="bx-right-arrow-alt" />
      </Link>
    </Panel>
  );
};

const GenericWidget = ({ widget }) => (
  <Panel
    title={
      widget.displayName ||
      registry[widget.type]?.displayName ||
      "Dashboard widget"
    }
  >
    <div className="dashboard-widget-placeholder">
      <Icon name={registry[widget.type]?.icon || "bx-widget"} />
      <p>
        {registry[widget.type]?.description ||
          "This widget is ready to be configured for your dashboard."}
      </p>
    </div>
  </Panel>
);

const sizes = (small, medium, large) => ({
  ...(small ? { small } : {}),
  ...(medium ? { medium } : {}),
  ...(large ? { large } : {}),
});

const define = (
  type,
  displayName,
  category,
  icon,
  supportedSizes,
  defaults = {},
  Component = GenericWidget,
  description = ""
) => ({
  type,
  displayName,
  category,
  icon,
  supportedSizes,
  defaultSize: Object.keys(supportedSizes)[0],
  defaultConfiguration: defaults,
  Component,
  description,
  configurable:
    type === "risk-distribution" ||
    type === "upcoming-deadlines" ||
    type === "query-analytics",
  removable: true,
});

export const registry = {
  ...designRegistry,
  "total-engagements": define(
    "total-engagements",
    "Total Engagements",
    "KPIs",
    "bx-folder-open",
    sizes(2),
    {},
    MetricWidget
  ),
  "active-engagements": define(
    "active-engagements",
    "Active Engagements",
    "KPIs",
    "bx-calendar-check",
    sizes(2),
    {},
    MetricWidget
  ),
  "assigned-to-me": define(
    "assigned-to-me",
    "Assigned to Me",
    "KPIs",
    "bx-user-check",
    sizes(2),
    {},
    MetricWidget
  ),
  "high-risk": define(
    "high-risk",
    "High Risk",
    "KPIs",
    "bx-bookmark-alt",
    sizes(2),
    {},
    MetricWidget
  ),
  "overdue-reviews": define(
    "overdue-reviews",
    "Overdue Reviews",
    "KPIs",
    "bx-time-five",
    sizes(2),
    {},
    MetricWidget
  ),
  "open-findings": define(
    "open-findings",
    "Open Findings",
    "KPIs",
    "bx-error-circle",
    sizes(2),
    {},
    MetricWidget
  ),
  "open-queries": define(
    "open-queries",
    "Open Queries",
    "KPIs",
    "bx-question-mark",
    sizes(2),
    {},
    MetricWidget
  ),
  "completed-reviews": define(
    "completed-reviews",
    "Completed Reviews",
    "KPIs",
    "bx-badge-check",
    sizes(2),
    {},
    MetricWidget
  ),
  "upcoming-deadlines-kpi": define(
    "upcoming-deadlines-kpi",
    "Upcoming Deadlines",
    "KPIs",
    "bx-calendar",
    sizes(2),
    {},
    MetricWidget
  ),
  "inspection-pipeline": define(
    "inspection-pipeline",
    "Inspection Pipeline",
    "Engagement & Inspection",
    "bx-git-branch",
    { medium: 6, large: 8 },
    {},
    InspectionPipeline,
    "Track work as it moves through the inspection workflow."
  ),
  "engagement-status": define(
    "engagement-status",
    "Engagement Status",
    "Engagement & Inspection",
    "bx-list-check",
    sizes(3, 4, 6),
    {},
    GenericWidget,
    "A summary of engagement statuses."
  ),
  "inspection-cycle-progress": define(
    "inspection-cycle-progress",
    "Inspection Cycle Progress",
    "Engagement & Inspection",
    "bx-line-chart",
    sizes(3, 4, 6),
    {},
    InspectionCycleProgress
  ),
  "engagements-by-type": define(
    "engagements-by-type",
    "Engagements by Type",
    "Engagement & Inspection",
    "bx-pie-chart-alt-2",
    sizes(3, 4, 6)
  ),
  "engagements-by-industry": define(
    "engagements-by-industry",
    "Engagements by Industry",
    "Engagement & Inspection",
    "bx-buildings",
    sizes(3, 4, 6)
  ),
  "recently-updated-engagements": define(
    "recently-updated-engagements",
    "Recently Updated Engagements",
    "Engagement & Inspection",
    "bx-refresh",
    sizes(3, 4, 6)
  ),
  "risk-distribution": define(
    "risk-distribution",
    "Risk Distribution",
    "Risk & Findings",
    "bx-doughnut-chart",
    sizes(3, 4, 6),
    {
      cycle: "current",
      riskLevels: ["high", "medium", "low", "notRated"],
      chartType: "donut",
      scope: "accessible",
    },
    RiskDistribution
  ),
  "findings-by-severity": define(
    "findings-by-severity",
    "Findings by Severity",
    "Risk & Findings",
    "bx-error",
    sizes(3, 4, 6)
  ),
  "findings-aging": define(
    "findings-aging",
    "Findings Aging",
    "Risk & Findings",
    "bx-time",
    sizes(3, 4, 6)
  ),
  "high-risk-engagements": define(
    "high-risk-engagements",
    "High Risk Engagements",
    "Risk & Findings",
    "bx-shield-quarter",
    sizes(3, 4, 6)
  ),
  "query-analytics": define(
    "query-analytics",
    "Query Analytics",
    "Queries",
    "bx-message-square-dots",
    sizes(3, 4, 6),
    {
      agingBuckets: ["0-7", "8-14", "15-30", "30+"],
      displayResponseTime: true,
      displayQueryAging: true,
    },
    QueryAnalytics
  ),
  "query-aging": define(
    "query-aging",
    "Query Aging",
    "Queries",
    "bx-time",
    sizes(3, 4, 6)
  ),
  "average-response-time": define(
    "average-response-time",
    "Average Response Time",
    "Queries",
    "bx-timer",
    sizes(2, 3, 4)
  ),
  "workload-by-reviewer": define(
    "workload-by-reviewer",
    "Workload by Reviewer",
    "Workload",
    "bx-group",
    { medium: 4, large: 6 },
    {},
    WorkloadByReviewer
  ),
  "reviewer-capacity": define(
    "reviewer-capacity",
    "Reviewer Capacity",
    "Workload",
    "bx-bar-chart-alt-2",
    sizes(3, 4, 6)
  ),
  "my-assignments": define(
    "my-assignments",
    "My Assignments",
    "Workload",
    "bx-task",
    sizes(3, 4, 6),
    {},
    MyWorkWidget
  ),
  "reviews-awaiting-me": define(
    "reviews-awaiting-me",
    "Reviews Awaiting Me",
    "Workload",
    "bx-user-voice",
    sizes(3, 4, 6),
    {},
    MyWorkWidget
  ),
  "upcoming-deadlines": define(
    "upcoming-deadlines",
    "Upcoming Deadlines",
    "Schedule",
    "bx-calendar-event",
    sizes(3, 4, 6),
    { daysAhead: 30, includeOverdue: true, maxItems: 5, riskLevel: "all" },
    UpcomingDeadlines
  ),
  "upcoming-reviews": define(
    "upcoming-reviews",
    "Upcoming Reviews",
    "Schedule",
    "bx-calendar-check",
    sizes(3, 4, 6)
  ),
  calendar: define(
    "calendar",
    "Calendar",
    "Schedule",
    "bx-calendar",
    sizes(3, 4, 6)
  ),
  "overdue-items": define(
    "overdue-items",
    "Overdue Items",
    "Schedule",
    "bx-error-circle",
    sizes(3, 4, 6)
  ),
};

export const widgetCatalog = Object.values(registry);
export const defaultDashboardWidgets = [
  ["total-engagements", "small"],
  ["active-engagements", "small"],
  ["high-risk", "small"],
  ["overdue-reviews", "small"],
  ["open-queries", "small"],
  ["completed-reviews", "small"],
  ["inspection-pipeline", "medium"],
  ["risk-distribution", "small"],
  ["upcoming-deadlines", "small"],
  ["workload-by-reviewer", "medium"],
  ["query-analytics", "medium"],
  ["inspection-cycle-progress", "medium"],
].map(([type, size], index) => ({
  id: `system-${type}`,
  type,
  size,
  sortOrder: index,
  configuration: { ...registry[type].defaultConfiguration },
}));

export const getColumns = (widget) =>
  registry[widget.type]?.supportedSizes?.[widget.size] || 4;
export const createWidget = (type, index) => ({
  id: `local-${type}-${Date.now()}`,
  type,
  size: registry[type].defaultSize,
  sortOrder: index,
  configuration: { ...registry[type].defaultConfiguration },
});
